import type { LucideIcon } from 'lucide-react';
import { MoreVertical, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * Represents a single action item in the CardHeaderActions component
 */
export interface ActionItem {
  /** Unique key for the action */
  key: string;
  /** Display label for the action */
  label: string;
  /** Optional icon to display */
  icon?: LucideIcon;
  /** Click handler for the action */
  onClick: () => void;
  /** Whether the action is in a loading state */
  isLoading?: boolean;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Whether the action should be hidden */
  hidden?: boolean;
  /** Optional tooltip text (shown on desktop only) */
  tooltip?: string;
  /** Button variant (defaults to 'outline') */
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost';
}

/**
 * Props for the CardHeaderActions component
 */
interface CardHeaderActionsProps {
  /** Array of action items to display */
  actions: ActionItem[];
}

/**
 * Responsive action buttons for card headers.
 * Displays inline buttons on desktop (md+) and collapses to a dropdown menu on mobile.
 */
export function CardHeaderActions({ actions }: CardHeaderActionsProps) {
  // Filter out hidden actions
  const visibleActions = actions.filter((action) => !action.hidden);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <>
      {/* Desktop: Inline buttons */}
      <div className="hidden md:flex items-center gap-2">
        <TooltipProvider delayDuration={300}>
          {visibleActions.map((action) => {
            const Icon = action.icon;
            const button = (
              <LoadingButton
                key={action.key}
                variant={action.variant ?? 'outline'}
                size="sm"
                onClick={action.onClick}
                isLoading={action.isLoading}
                disabled={action.disabled}
              >
                {Icon && <Icon className="h-4 w-4" />}
                {action.label}
              </LoadingButton>
            );

            if (action.tooltip) {
              return (
                <Tooltip key={action.key}>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent>{action.tooltip}</TooltipContent>
                </Tooltip>
              );
            }

            return button;
          })}
        </TooltipProvider>
      </div>

      {/* Mobile: Dropdown menu */}
      <div className="md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {visibleActions.map((action) => {
              const Icon = action.icon;
              return (
                <DropdownMenuItem
                  key={action.key}
                  onClick={action.onClick}
                  disabled={action.disabled ?? action.isLoading}
                >
                  {action.isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : Icon ? (
                    <Icon className="h-4 w-4 mr-2" />
                  ) : null}
                  {action.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
